import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAlert } from "react-alert";
import {
  AttachFile,
  ChatBubbleOutline,
  Delete,
  ErrorOutline,
  InsertDriveFileOutlined,
  PersonOutline,
  Refresh,
  SchoolOutlined,
  Search,
  Send,
} from "@material-ui/icons";
import { useParent } from "../../../context/ParentContext";
import { baseUrl } from "../../../services/config";
import "./MonthlyStudentAttendance/parent-progress.css";
import "./teacher-chat.css";
import useAdaptiveChatPolling from "../TeacherChat/useAdaptiveChatPolling";

const getStudentId = (child) =>
  child?.student?.id || child?.student_id || child?.id || null;

const fullName = (person) =>
  [person?.first_name, person?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim() ||
  person?.name ||
  "Unknown";

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-PK", {
    day: "2-digit",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function fileUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${baseUrl.replace(/\/api\/?$/, "")}/storage/${path}`;
}

function subjectSummary(subjects) {
  if (!subjects.length) return "Teacher";
  return subjects
    .map((subject) => subject.slug)
    .filter(Boolean)
    .join(", ");
}

function uniqueTeachers(teachers) {
  return teachers.map((teacher) => {
    const assignments = Array.isArray(teacher.teacher_assignments)
      ? teacher.teacher_assignments
      : [];
    const seenSubjects = new Set();
    const subjects = assignments.reduce((items, assignment) => {
      const subjectId = assignment.subject?.id || assignment.subject_id || "";
      const subjectSlug =
        assignment.subject?.slug ||
        assignment.subject?.name ||
        (subjectId ? String(subjectId) : "");
      const subjectKey = String(subjectId || subjectSlug);
      if (!subjectKey || seenSubjects.has(subjectKey)) return items;
      seenSubjects.add(subjectKey);
      items.push({
        id: subjectId,
        name: assignment.subject?.name || subjectSlug,
        slug: subjectSlug,
      });
      return items;
    }, []);
    return {
      key: String(teacher.id),
      teacher,
      subjects,
      title: fullName(teacher),
      subtitle: subjectSummary(subjects),
    };
  });
}

export default function TeacherChat() {
  const alert = useAlert();
  const { selectedChild } = useParent();
  const studentId = getStudentId(selectedChild);
  const studentName = selectedChild?.student?.name || "Student";
  const [teachers, setTeachers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [history, setHistory] = useState(null);
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [search, setSearch] = useState("");
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [refreshingChat, setRefreshingChat] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const teacherContacts = useMemo(() => uniqueTeachers(teachers), [teachers]);
  const totalUnreadCount = useMemo(
    () =>
      conversations.reduce(
        (total, conversation) => total + Number(conversation.unread_count || 0),
        0,
      ),
    [conversations],
  );
  const filteredTeacherContacts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return teacherContacts;
    return teacherContacts.filter((item) =>
      `${item.title} ${item.subtitle}`.toLowerCase().includes(query),
    );
  }, [search, teacherContacts]);

  const messages = history?.messages || [];
  const activeConversation = history?.conversation || activeChat?.conversation;
  const findConversation = (teacherId) =>
    conversations.find(
      (conversation) =>
        Number(conversation.teacher_id) === Number(teacherId) &&
        Number(conversation.student_id) === Number(studentId),
    );

  const loadConversations = async (token) => {
    const response = await fetch(`${baseUrl}/conversations`, {
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    const result = await response.json();
    if (!response.ok) {
      throw new Error(
        result?.metadata?.message || "Unable to load conversations.",
      );
    }
    const studentConversations = (Array.isArray(result?.payload)
      ? result.payload
      : []
    ).filter(
      (conversation) => Number(conversation.student_id) === Number(studentId),
    );
    setConversations(studentConversations);
    return studentConversations;
  };

  const loadPeople = async () => {
    if (!studentId) {
      setTeachers([]);
      setConversations([]);
      setActiveChat(null);
      setHistory(null);
      return;
    }
    setLoadingPeople(true);
    setError("");
    try {
      const token = localStorage.getItem("parentToken");
      const [peopleResponse] = await Promise.all([
        fetch(`${baseUrl}/chat-people`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ student_id: studentId }),
        }),
        loadConversations(token),
      ]);
      const peopleResult = await peopleResponse.json();
      if (!peopleResponse.ok) {
        throw new Error(
          peopleResult?.metadata?.message || "Unable to load teachers.",
        );
      }
      setTeachers(
        Array.isArray(peopleResult?.payload) ? peopleResult.payload : [],
      );
      setActiveChat(null);
      setHistory(null);
    } catch (requestError) {
      const messageText =
        requestError.message || "Unable to load teacher chat data.";
      setError(messageText);
      alert.error(messageText);
    } finally {
      setLoadingPeople(false);
    }
  };

  const loadHistory = async (conversation, options = {}) => {
    if (!conversation?.id) {
      setHistory(null);
      return;
    }
    if (!options.silent) setLoadingHistory(true);
    try {
      const token = localStorage.getItem("parentToken");
      const response = await fetch(`${baseUrl}/chat-history`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ conversation_id: conversation.id }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(
          result?.metadata?.message || "Unable to load chat history.",
        );
      }
      setHistory(result?.payload || null);
    } catch (requestError) {
      if (!options.silent) {
        alert.error(requestError.message || "Unable to load chat history.");
        setHistory(null);
      }
    } finally {
      if (!options.silent) setLoadingHistory(false);
    }
  };

  const refreshChatData = useCallback(async (options = {}) => {
    if (!studentId) return;
    const token = localStorage.getItem("parentToken");
    const latestConversations = await loadConversations(token);
    if (!activeChat?.teacher?.id) return;
    const conversation = latestConversations.find(
      (item) =>
        Number(item.teacher_id) === Number(activeChat.teacher.id) &&
        Number(item.student_id) === Number(studentId),
    );
    if (conversation) {
      setActiveChat((currentChat) =>
        currentChat?.teacher?.id === activeChat.teacher.id
          ? { ...currentChat, conversation }
          : currentChat,
      );
      await loadHistory(conversation, options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat?.teacher?.id, studentId]);

  const handleRefreshChat = async () => {
    setRefreshingChat(true);
    try {
      await refreshChatData();
    } catch (requestError) {
      alert.error(requestError.message || "Unable to refresh chat.");
    } finally {
      setRefreshingChat(false);
    }
  };

  const selectTeacher = (item) => {
    const conversation = findConversation(item.teacher.id);
    setActiveChat({ ...item, conversation });
    setMessage("");
    setFile(null);
    if (conversation) loadHistory(conversation);
    else setHistory(null);
  };

  const resizeMessageInput = () => {
    const input = messageInputRef.current;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${input.scrollHeight}px`;
  };

  const handleMessageChange = (event) => {
    setMessage(event.target.value);
    resizeMessageInput();
  };

  const sendMessage = async (event) => {
    event.preventDefault();
    if (!activeChat || !studentId || sending) return;
    const trimmedMessage = message.trim();
    if (!trimmedMessage && !file) {
      alert.error("Please write a message or attach a file.");
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem("parentToken");
      const formData = new FormData();
      formData.append("student_id", studentId);
      formData.append("teacher_user_id", activeChat.teacher.id);
      formData.append("message", trimmedMessage);
      if (file) formData.append("file", file);

      const response = await fetch(`${baseUrl}/send-message`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.metadata?.message || "Unable to send message.");
      }
      setMessage("");
      setFile(null);
      const latestConversations = await loadConversations(token);
      const conversation =
        result?.payload?.conversation ||
        latestConversations.find(
          (item) =>
            Number(item.teacher_id) === Number(activeChat.teacher.id) &&
            Number(item.student_id) === Number(studentId),
        );
      if (conversation) {
        setActiveChat({ ...activeChat, conversation });
        await loadHistory(conversation);
      }
      alert.success(result?.metadata?.message || "Message sent successfully.");
    } catch (requestError) {
      alert.error(requestError.message || "Unable to send message.");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    loadPeople();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  useAdaptiveChatPolling({
    enabled: Boolean(studentId),
    hasActiveConversation: Boolean(activeConversation?.id),
    onPoll: () => refreshChatData({ silent: true }),
  });

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length, loadingHistory]);

  useEffect(() => {
    resizeMessageInput();
  }, [message]);

  return (
    <section className="parent-progress-page parent-chat-page">
      <div className="parent-progress-hero parent-chat-hero">
        <div>
          <span className="parent-progress-eyebrow">Teacher communication</span>
          <h1>Teacher Chat</h1>
          <p>
            {selectedChild
              ? `Message class and subject teachers for ${studentName}.`
              : "Select a child to start a teacher conversation."}
          </p>
        </div>
        <div className="parent-chat-hero-stats">
          <div className="parent-progress-hero-stat">
            <span>{teacherContacts.length}</span>
            <small>Contacts</small>
          </div>
          <div className="parent-progress-hero-stat parent-chat-unread-stat">
            <span>{totalUnreadCount}</span>
            <small>Unread</small>
          </div>
        </div>
      </div>

      {!studentId ? (
        <PageState
          icon={SchoolOutlined}
          title="Student unavailable"
          text="Please select a child to view teacher chat."
        />
      ) : loadingPeople ? (
        <PageState
          loading
          title="Loading teachers"
          text="Getting teacher contacts and conversations."
        />
      ) : error ? (
        <PageState
          icon={ErrorOutline}
          error
          title="Teacher chat unavailable"
          text={error}
          onRetry={loadPeople}
        />
      ) : (
        <div className="parent-chat-shell">
          <aside className="parent-chat-contacts">
            <label className="parent-chat-search">
              <Search />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search teacher or subject"
                aria-label="Search teacher or subject"
              />
            </label>
            <div className="parent-chat-contact-list">
              {filteredTeacherContacts.length ? (
                filteredTeacherContacts.map((item) => {
                  const conversation = findConversation(item.teacher.id);
                  const unreadCount = Number(conversation?.unread_count || 0);
                  const isActive = activeChat?.teacher?.id === item.teacher.id;
                  return (
                    <button
                      type="button"
                      className={`parent-chat-contact${
                        isActive ? " active" : ""
                      }`}
                      key={item.key}
                      onClick={() => selectTeacher(item)}
                    >
                      <span className="parent-chat-avatar">
                        <PersonOutline />
                      </span>
                      <span className="parent-chat-contact-copy">
                        <strong>{item.title}</strong>
                        <small>{item.subtitle}</small>
                      </span>
                      {conversation && (
                        <span
                          className={`parent-chat-contact-status${
                            unreadCount > 0 ? " unread" : ""
                          }`}
                        >
                          {unreadCount > 0 ? unreadCount : "Open"}
                        </span>
                      )}
                    </button>
                  );
                })
              ) : (
                <div className="parent-chat-empty-contacts">
                  <ChatBubbleOutline />
                  <p>No teachers found.</p>
                </div>
              )}
            </div>
          </aside>
          <div className="parent-chat-panel">
            {activeChat ? (
              <>
                <header className="parent-chat-panel-header">
                  <div>
                    <h2>{activeChat?.title}</h2>
                    <p>{activeChat?.subtitle}</p>
                  </div>
                  <button
                    type="button"
                    className="parent-chat-refresh-button"
                    onClick={handleRefreshChat}
                    disabled={refreshingChat || loadingHistory}
                    aria-label="Refresh chat"
                    title="Refresh chat"
                  >
                    <Refresh />
                  </button>
                  {/* {activeConversation?.id && (
                    <span>Conversation #{activeConversation.id}</span>
                  )} */}
                </header>
                <div className="parent-chat-messages">
                  {loadingHistory ? (
                    <PageState
                      compact
                      loading
                      title="Loading history"
                      text="Fetching conversation messages."
                    />
                  ) : messages.length ? (
                    messages.map((item) => {
                      const isParent =
                        item.sender_type === "parent" ||
                        Number(item.sender_id) ===
                          Number(activeConversation?.parent_id);
                      return (
                        <article
                          className={`parent-chat-message${
                            isParent ? " parent" : " teacher"
                          }`}
                          key={item.id}
                        >
                          <div>
                            <p>{item.message || "Attachment"}</p>
                            {item.file_path && (
                              <a
                                href={fileUrl(item.file_path)}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <InsertDriveFileOutlined />
                                {item.file_type || "Attachment"}
                              </a>
                            )}
                            <time>{formatDateTime(item.created_at)}</time>
                          </div>
                        </article>
                      );
                    })
                  ) : (
                    <div className="parent-chat-start">
                      <ChatBubbleOutline />
                      <h3>Start the conversation</h3>
                      <p>Send a message to create a chat with this teacher.</p>
                    </div>
                  )}
                  <span ref={messagesEndRef} />
                </div>
                <form className="parent-chat-composer" onSubmit={sendMessage}>
                  {file && (
                    <div className="parent-chat-file-chip">
                      <InsertDriveFileOutlined />
                      <span>{file.name}</span>
                      <button type="button" onClick={() => setFile(null)}>
                        <Delete />
                      </button>
                    </div>
                  )}
                  <div className="parent-chat-composer-row">
                    <textarea
                      ref={messageInputRef}
                      value={message}
                      onChange={handleMessageChange}
                      placeholder="Type your message"
                      rows={1}
                    />
                    <label className="parent-chat-file-button">
                      <AttachFile />
                      <input
                        type="file"
                        onChange={(event) =>
                          setFile(event.target.files?.[0] || null)
                        }
                      />
                    </label>
                    <button
                      type="submit"
                      className="parent-chat-send-button"
                      disabled={sending}
                      aria-label={sending ? "Sending message" : "Send message"}
                      title={sending ? "Sending message" : "Send message"}
                    >
                      <Send />
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="parent-chat-select-state">
                <ChatBubbleOutline />
                <h2>Select a teacher</h2>
                <p>Choose a teacher to view or start a chat.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function PageState({
  icon: Icon,
  title,
  text,
  loading,
  error,
  onRetry,
  compact,
}) {
  return (
    <div
      className={`parent-chat-state${error ? " error" : ""}${
        compact ? " compact" : ""
      }`}
      role={error ? "alert" : "status"}
    >
      {loading ? <span className="parent-chat-spinner" /> : Icon && <Icon />}
      <h2>{title}</h2>
      <p>{text}</p>
      {onRetry && (
        <button type="button" onClick={onRetry}>
          <Refresh /> Try again
        </button>
      )}
    </div>
  );
}
